import type { Choice, Requirement, Session } from "@sdcp/api-client";
import { useRef, useState } from "react";

import { Button } from "@sdcp/ui";

import { ConnectBankButton } from "./ConnectBankButton";
import { UploadButton } from "./UploadButton";

export interface ComposerProps {
  choice: Choice;
  /** The requirement the primary action acts on, when there is one. */
  requirement?: Requirement | undefined;
  onPrimary: () => void;
  onSkip: () => void;
  onUpload: (file: File) => void;
  onConnected: (session: Session) => void;
  onError: (message: string) => void;
  busy?: boolean;
  /**
   * Something the borrower typed.
   *
   * Secondary to the buttons on purpose — most of a session is "yes, that one",
   * and typing it would be worse. This is for what a checklist cannot hold:
   * "my statement isn't out until the 5th", "I bank with a credit union".
   */
  onSay?: (text: string) => void;
  error?: string | null;
}

/**
 * The action zone and the composer (FR-4, FR-6).
 *
 * The bottom is a slim row of **suggested replies** riding directly on the
 * always-visible composer: the things the borrower is most likely to say next,
 * outlined, left-aligned, scrolling sideways, at most three. The first is the
 * primary and says so with a firmer ring and nothing else — filled purple is
 * the borrower's own sent messages, and a suggestion in it reads as one.
 *
 * The chips are ephemeral. They leave while the borrower types, because a
 * person composing a sentence has already declined the shortcuts, and the row
 * would otherwise sit between their thumb and the field.
 *
 * FR-4 names two predecessors not to resurrect. One was this component until
 * now: a stacked full-width CTA with a secondary beneath it above the composer.
 * Full-width CTAs survive only at commitment moments — the gate, a sign-in
 * sheet, completion — none of which is a reply.
 */
export function Composer({
  choice,
  requirement,
  onPrimary,
  onSkip,
  onUpload,
  onConnected,
  onError,
  busy = false,
  error,
  onSay,
}: ComposerProps) {
  const [said, setSaid] = useState("");
  // The "+" has its own file input. It used to borrow whichever one a chip
  // happened to render, and the chips leave while the borrower types — so the
  // one control still on screen for adding a document silently did nothing.
  const addFile = useRef<HTMLInputElement>(null);

  const send = () => {
    const text = said.trim();
    if (!text || busy) return;
    setSaid("");
    onSay?.(text);
  };

  // Plaid requirements open Link rather than posting straight to the server —
  // the borrower has to choose their bank and sign in first.
  const usesPlaid = requirement?.method === "plaid" || requirement?.method === "plaid_income";

  // When uploading *is* the path, the primary has to open a file picker. It
  // used to call the retrieval endpoint while reading "Upload pay stubs",
  // which moved the requirement to `retrieving` and never asked for a file —
  // leaving no way at all to satisfy an upload-only requirement.
  const isUpload = requirement?.method === "upload";

  const typing = said.trim().length > 0;

  return (
    <div className="shrink-0 border-t border-border bg-surface pt-2 pb-3">
      {!typing && (
        <div
          role="group"
          aria-label="Suggested replies"
          className="flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [mask-image:linear-gradient(90deg,#000_92%,transparent)] [&::-webkit-scrollbar]:hidden"
        >
          {usesPlaid && choice.requirement_id ? (
            <ConnectBankButton
              requirementId={choice.requirement_id}
              label={choice.primary}
              variant="chip-primary"
              busy={busy}
              onConnected={onConnected}
              onUnavailable={onError}
            />
          ) : isUpload ? (
            <UploadButton
              label={choice.primary}
              variant="chip-primary"
              busy={busy}
              onPick={onUpload}
            />
          ) : (
            <Button variant="chip-primary" onClick={onPrimary} disabled={busy}>
              {choice.primary}
            </Button>
          )}

          {/* A different way to satisfy the same requirement. Absent when the
              primary already is the upload — there is nothing else to offer. */}
          {choice.secondary && (
            <UploadButton label={choice.secondary} variant="chip" busy={busy} onPick={onUpload} />
          )}

          {/* Setting it aside is a different decision from changing how it
              arrives, so it is its own reply rather than a footnote under the
              others. Last, because it is the one that moves nothing forward. */}
          {choice.can_skip && (
            <Button variant="chip" onClick={onSkip} disabled={busy}>
              Skip for now
            </Button>
          )}
        </div>
      )}

      {/* Rejection text comes from the server already written for a borrower. */}
      {error && (
        <p role="alert" className="mt-1 px-4 text-sm text-err-600">
          {error}
        </p>
      )}

      <input
        ref={addFile}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/*"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        data-testid="composer-file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so the same file picked twice still fires — which is what
          // happens after a rejected upload.
          e.target.value = "";
          if (file) onUpload(file);
        }}
      />

      <div className="mt-2 flex items-center gap-2.5 px-3">
        <button
          type="button"
          aria-label="Add a document"
          disabled={busy}
          onClick={() => addFile.current?.click()}
          className="grid size-11 shrink-0 place-items-center rounded-full border border-border-soft text-xl text-verity-600 transition-colors hover:border-verity-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verity-ring"
        >
          +
        </button>

        <div className="flex flex-1 items-center gap-2 rounded-full border border-border-soft px-4 py-2.5 focus-within:border-verity-300">
          {/* Live since the message endpoint shipped. It sat disabled until
              then, because an input that silently swallows a borrower's
              question is worse than one that says it is not ready.

              It no longer locks while Verity replies. A turn is fifteen
              seconds of model round-trips, and a field that refuses
              keystrokes for that long reads as the app ignoring you. Typing
              stays open; only the send waits, and the thread shows why. */}
          <input
            value={said}
            onChange={(e) => setSaid(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                send();
              }
            }}
            maxLength={1000}
            // FR-9. No hint tail: the chips above already say what she can do.
            placeholder="Message Verity"
            aria-label="Message Verity"
            className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-400 disabled:cursor-not-allowed"
          />
          {said.trim() && (
            <button
              type="button"
              onClick={send}
              disabled={busy}
              aria-label="Send"
              className="grid size-7 shrink-0 place-items-center rounded-full text-white disabled:opacity-40"
              style={{ background: "var(--brand-grad)" }}
            >
              <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
                <path
                  d="M4 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {/* Voice is deferred — the control is present because the prototype
              shows it, and hidden from assistive tech until it works. It gives
              way to the send button once there is something to send. */}
          <span
            aria-hidden
            hidden={Boolean(said.trim())}
            className="flex items-end gap-0.5 opacity-40"
            title="Voice — coming soon"
          >
            {[6, 12, 9, 14, 7].map((h, i) => (
              <span
                key={i}
                className="w-0.5 rounded-full bg-verity-500"
                style={{ height: `${h}px` }}
              />
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}
